import React, {useMemo, SetStateAction, useContext} from 'react';
import styles from './styles/FilesFoldersBlock.module.scss';
import classNames from 'classnames';
import FontAwesome from "../../../utilities/FontAwesome";
import FilesListHeader from "./FilesListHeader";
import CleverTable from "../../../utilities/CleverTable/CleverTable";
import ImageComponent from "../../../utilities/ImageComponent";
import {getIconByMimeType, getIconSet} from "./fileUtilities";
import moment from "moment";
import ClickableLink from "../../../utilities/ClickableLink";
import {observer} from "mobx-react";
import {StoreContext} from "../../../../stores/StoreLoader";
import {EditButton} from "../EditButton";
import {canPreviewContentItem} from "./FilesFoldersBlock";


const tableCellClassName = classNames({
    [styles.tableCell]: true,
});

const transitionClassNames = {
    enter: styles.transitionEnter,
    enterActive: styles.transitionEnterActive,
    enterDone: styles.transitionEnterDone,
    exit: styles.transitionExit,
    exitActive: styles.transitionExitActive,
    exitDone: styles.transitionExitDone,
};

const FilesList = observer((props: {
    files_folders: Array<FileContentItem | FolderContentItem>,
    setSelectedContentItem: SetStateAction<any>,
    selectedItem?: FileContentItem | FolderContentItem,
    aboveTheFold: boolean,
    handleFetchMore?: () => void,
    shouldFetchMore?: boolean,
    maxHeight?: number | string,
    useInfiniteScroll?: boolean,
    handleSave: (contentItem: ContentItem, organization: BaseOrganization) => void,
    handleDelete: (contentItem: ContentItem) => void,
}) => {
    const {organizationStore, modalStore, interfaceStore, userStore} = useContext(StoreContext);

    const displayDescriptionColumn = props.files_folders.some(f => f.description);

    const filesContainerClassName = classNames({
        [styles.filesSliderContainer]: true,
        [styles.filesSliderContainerBackgroundImage]: !!organizationStore.currentOrganization.backgroundImage,
    });

    const columns = useMemo(() => {
        let c = [{
            title: <FilesListHeader name={'Name'} />,
            dataKey: 'name',
            initialWidth: 65,
        }]
        if (displayDescriptionColumn) {
            c[0].initialWidth = 50;
            c.splice(1, 0, {
                title: <FilesListHeader name={'Description'} />,
                dataKey: 'description',
                initialWidth: 50,
            })
        }
        if (interfaceStore.breakpoint === "break-point-xs") {
            c = [{
                title: <FilesListHeader name={'Name'} />,
                dataKey: 'name',
                initialWidth: 100,
            }];
        }
        return c;
    }, [displayDescriptionColumn, interfaceStore.breakpoint]);

    return (
        <CleverTable
            columns={columns}
            transitionIn={props.aboveTheFold}
            useInfiniteScroll={props.useInfiniteScroll}
            transitionClassNames={transitionClassNames}
            data={props.files_folders.map((file, idx) => {
                const fileOrigin = getIconSet(file.type);
                const fileOrFolderSettings = file.is_file ? (file as FileContentItem).json_data.file : (file as FolderContentItem).json_data.folder;
                const iconUrl = getIconByMimeType(fileOrigin, file.is_folder ? 'application/vnd.google-apps.folder' : file.json_data.file?.mime_type);
                const shouldOpenPreview = canPreviewContentItem(file, organizationStore.organization);
                return {
                    name: <div className={tableCellClassName}>
                        <ImageComponent src={iconUrl}/>
                        {shouldOpenPreview ? <span>{file.title}</span> : <>
                            <ClickableLink href={fileOrFolderSettings?.view_link}>
                                <span>{file.title}</span>
                                <FontAwesome name={'fa-external-link-alt'} prefix={'fas'}/>
                            </ClickableLink>
                        </>}
                        <EditButton contentItem={file} handleSave={props.handleSave} handleDelete={props.handleDelete} />
                    </div>,
                    description: <div className={tableCellClassName}>
                        {file.description}
                    </div>,
                    modified: <div className={tableCellClassName}>
                        {file.is_file ? moment(file.is_custom ? file.updated_at : fileOrFolderSettings.updated_at).fromNow() : <p></p>}
                    </div>,
                    onClick: shouldOpenPreview ? () => {
                        if (interfaceStore.dimensions.width < 1000) {
                            modalStore.addModal({
                                type: 'file-preview',
                                id: file.id,
                                itemObj: file,
                            })
                        } else {
                            props.setSelectedContentItem(file);
                        }
                    } : false,
                    key: file.id,
                    style: {
                        transitionDelay: `${idx * .15}s`,
                    },
                };
            })}
            className={filesContainerClassName}
            tableRowClassName={styles.tableRow}
            noDataMessage={"No files or folders found."}
            initialWidthIsPercent={true}
            selectedItemKey={props.selectedItem?.id}
            handleFetchMore={props.handleFetchMore}
            shouldFetchMore={props.shouldFetchMore}
            maxHeight={props.maxHeight}
        />
    );
});

export default FilesList;
